import { Scrollbar } from "@carbon6/c6-frontend-design";
import { useLayoutEffect, useState, type ReactNode } from "react";
import { nativeEnterRegionProps } from "@/components/keyboard";
import type { EpisodeSummary } from "@/api";
import { EmptyState } from "@/shared/ui/emptyState/public";
import type { FileBrowserSource } from "../../fileBrowserSource";
import type { EpisodeNav } from "../useEpisodeNav";
import type { FolderSelection } from "../selection";
import { ChannelVideoPreview, EpisodePreview, type EpisodePlayback } from "./EpisodeVideoPreview";
import { FilePane } from "./FilePane";
import { selectionHeadline } from "./previewSelection";
import styles from "../FileBrowser.module.css";

// 재생 바 영역이 비어 있는 동안 유지할 높이(마지막으로 렌더링된 바의 높이). `FileBrowser.module.css` 의
// `.transportBar:empty`.
const TRANSPORT_BAR_HELD_HEIGHT_VAR = "--transport-bar-held-height";

export type PreviewPaneProps = {
  source: FileBrowserSource;
  datasetUuid: string;
  selection: FolderSelection;
  // 리뷰 표면이 붙을 에피소드(없으면 null). 판정은 `previewSelection` 이 소유한다. 브라우저가
  // 한 번 계산하므로 좌측 목록 하이라이트와 이 pane 이 같은 답을 쓴다.
  reviewEpisode: EpisodeSummary | null;
  canModify: boolean;
  // extra 파일 write(이름변경·삭제)가 성공하면 호출한다. 부모는 트리와 파일 목록을 다시 조회한다.
  onChanged: () => void;
  nav: EpisodeNav;
  renderEpisodeExtra?: (episode: EpisodeSummary, playback: EpisodePlayback) => ReactNode;
  renderEpisodeFooter?: (episode: EpisodeSummary, nav: EpisodeNav) => ReactNode;
  analysisLayout?: boolean;
};

/**
 * 우측 미리보기 pane. 무엇을 그릴지와 리뷰 표면이 붙는지를 선택 하나로 정한다.
 *
 * 이 컴포넌트가 세로 단과 그 계약을 소유한다. 세로 단은 다음 순서다.
 * - 고정 머리(제목 줄)
 * - 본문
 * - 바닥(리뷰 독)
 * - 에피소드 재생 블록(칩 줄 + 재생 바)
 * `.main` 을 이 컴포넌트가 직접 렌더해야 `flex:1 1 auto; min-height:0` 사슬이 이어진다. 그래야
 * 바닥이 잘리지 않는다. 부모가 위에 div 를 추가하면 스크롤이 이중으로 생긴다.
 *
 * 에피소드 본문은 바깥에서 스크롤하지 않는다. 영상 격자가 남은 높이에 맞춰 줄고, 하한 아래에서는
 * 격자가 자기 상자 안에서 스크롤한다. 높이가 모자라면 독이 먼저 줄고 자기 안에서 스크롤한다.
 * 그래서 영상이 하한을 유지한다. 파일·단일 카메라 미리보기는 본문이 스크롤한다.
 *
 * 이 pane 이 필요한 이유는 재생 블록을 독 **아래**에 두기 위해서다. 재생 바는 영상 플레이어가
 * 그린다. 그러나 영상과 바 사이에 독이 있으므로, 이 pane 이 바의 영역을 만들고 플레이어는 그
 * 영역으로 portal 한다.
 *
 * 좌측 목록 모드(에피소드/파일)는 이 pane 에 전달되지 않는다. 미리보기는 오직 `selection` 이
 * 정한다. 목록 모드와 선택은 여기서 서로 독립이다.
 */
export function PreviewPane({
  source,
  datasetUuid,
  selection,
  reviewEpisode,
  canModify,
  onChanged,
  nav,
  renderEpisodeExtra,
  renderEpisodeFooter,
  analysisLayout = false,
}: PreviewPaneProps): ReactNode {
  // 에피소드 스코프 슬롯은 리뷰가 붙는 선택일 때만 호출한다. 소비처가 "이 대상은 리뷰가
  // 불가능하다"를 매번 다시 방어하지 않게 이 계층이 판정을 소유한다. 슬롯이 null 을 돌려주면
  // 컨테이너도 그리지 않는다(로딩 중 등).
  const footer =
    reviewEpisode === null ? null : (renderEpisodeFooter?.(reviewEpisode, nav) ?? null);
  const headline = selectionHeadline(selection);
  // 재생 블록의 두 영역. 마운트된 요소를 state 로 보관해야, 마운트 순간 플레이어·슬롯이 portal 을 연다.
  const [transportLead, setTransportLead] = useState<HTMLDivElement | null>(null);
  const [transportBar, setTransportBar] = useState<HTMLDivElement | null>(null);
  useHeldHeight(transportBar);
  const episodeSelected = selection?.kind === "episode";

  return (
    <section className={styles.main} aria-label="미리보기">
      {/* 에피소드 선택에서는 headline 이 null 이다. 그 대상의 머리 줄은 아래 리뷰 독이 소유한다. */}
      {headline === null ? null : (
        <div className={styles.header}>
          <span className={styles.badge}>{headline.badge}</span>
          <span className={styles.headerName}>{headline.name}</span>
          <span className={styles.headerMeta}>{headline.meta}</span>
        </div>
      )}
      {selection?.kind === "episode" ? (
        <div
          className={styles.episodeBody}
          data-layout={analysisLayout ? "analysis" : "review"}
          // 영상·진단 열의 버튼에서 Enter 는 그 버튼이 처리한다. 독의 저장 단축키는 동작하지 않는다.
          {...nativeEnterRegionProps}
          aria-label="미리보기 내용"
        >
          <EpisodePreview
            source={source}
            episode={reviewEpisode ?? selection.episode}
            analysisLayout={analysisLayout}
            transportBar={transportBar}
            transportLead={transportLead}
            renderExtra={renderEpisodeExtra}
          />
        </div>
      ) : (
        <Scrollbar className={styles.mainBody} gutter="auto" aria-label="미리보기 내용">
          {selection === null ? (
            <div className={styles.detailEmpty}>
              <EmptyState>항목을 선택해 주세요.</EmptyState>
            </div>
          ) : selection.kind === "channelVideo" ? (
            <ChannelVideoPreview
              source={source}
              episode={selection.episode}
              channel={selection.channel}
            />
          ) : (
            <FilePane
              source={source}
              datasetUuid={datasetUuid}
              file={selection.file}
              canModify={canModify}
              onChanged={onChanged}
            />
          )}
        </Scrollbar>
      )}
      {/* 슬롯이 비면 컨테이너도 지운다. 빈 구분선만 남으면 미리보기가 잘린 것처럼 보인다.
          높이가 모자라면 독이 먼저 줄고 자기 안에서 스크롤한다. 그래서 영상과 재생 블록을 덮지 않는다.
          스크롤은 독이 소유한다. 독은 머리 줄을 스크롤 밖에 두고 본문만 스크롤한다. */}
      {footer === null ? null : (
        <div className={styles.contentFooter} hidden={analysisLayout}>
          {footer}
        </div>
      )}
      {/* 칩 줄 → 재생 바. 칩은 바의 출처 행과 같은 시간을 가리키므로 바 바로 위에 둔다. */}
      {episodeSelected ? (
        <div className={styles.transport} {...nativeEnterRegionProps} aria-label="에피소드 재생">
          <div ref={setTransportLead} className={styles.transportLead} />
          <div ref={setTransportBar} className={styles.transportBar} />
        </div>
      ) : null}
    </section>
  );
}

/**
 * 비어 있는 동안 마지막 높이를 유지하는 영역.
 *
 * 재생 바는 에피소드를 넘길 때마다 잠깐 사라진다. 플레이어가 새 에피소드의 파일 목록을 받는
 * 동안 바를 언마운트하기 때문이다. 그 사이 영역이 접히면 위의 독이 바 높이만큼 내려갔다 올라온다.
 * 바의 높이는 폭에 따른 줄바꿈으로 달라지므로 고정값을 둘 수 없다. 그래서 렌더링된 바의 높이를
 * 재어 두고, 비어 있는 동안만 그 높이를 쓴다. CSS 는 비었을 때만 변수를 읽는다. 그래서 이 쓰기가
 * 크기를 다시 바꾸지 않는다.
 */
function useHeldHeight(element: HTMLElement | null): void {
  useLayoutEffect(() => {
    if (element === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (element.childElementCount === 0) return;
      element.style.setProperty(TRANSPORT_BAR_HELD_HEIGHT_VAR, `${element.offsetHeight}px`);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
}
